import { useEffect, useState } from "react";
import api, { formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Zap, ArrowLeft, Send, Plus, CheckCircle2, Clock } from "lucide-react";

export const SupportPanel = ({ onActivity }) => {
  const [meta, setMeta] = useState({ priority: false, tier: "" });
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null); // ticket detail
  const [composing, setComposing] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTickets = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {});
  const loadMeta = () => api.get("/support/meta").then((r) => setMeta(r.data)).catch(() => {});

  useEffect(() => { loadMeta(); loadTickets(); }, []);

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActive(data);
      loadTickets();
      onActivity && onActivity();
    } catch { toast.error("Could not open ticket"); }
  };

  const submitNew = async () => {
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", { subject, body });
      toast.success(meta.priority ? "Sent — you're in the priority queue" : "Support request sent");
      setSubject(""); setBody(""); setComposing(false);
      setActive(data); loadTickets();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${active.id}/messages`, { body: reply });
      setReply("");
      openTicket(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const PriorityHeader = () => (
    <div data-testid="support-priority-header" className={`rounded-xl border p-4 flex items-start gap-3 ${meta.priority ? "border-forest/25 bg-forest/[0.04]" : "border-line bg-stone-50"}`}>
      {meta.priority ? <Zap className="h-5 w-5 text-forest mt-0.5 shrink-0" /> : <LifeBuoy className="h-5 w-5 text-mute mt-0.5 shrink-0" />}
      <div>
        {meta.priority ? (
          <>
            <p className="text-sm font-medium text-ink flex items-center gap-2">Priority Support
              <span data-testid="priority-badge" className="text-[10px] font-semibold text-forest bg-forest/10 border border-forest/20 rounded-full px-2 py-0.5 uppercase tracking-wide">Pro</span>
            </p>
            <p className="text-xs text-mute mt-0.5">As a Pro member your requests jump to the top of our reply queue{meta.sla_target_hours ? <> — <span className="font-medium text-forest">target first response within {meta.sla_target_hours} hours</span></> : ""}.</p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-ink">Support</p>
            <p className="text-xs text-mute mt-0.5">We're here to help. <span className="text-forest font-medium">Upgrade to Pro</span> to skip the line with priority support and faster responses.</p>
          </>
        )}
      </div>
    </div>
  );

  // Thread view
  if (active) {
    return (
      <div className="max-w-2xl space-y-4">
        <button data-testid="support-back-btn" onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-sm text-mute hover:text-ink"><ArrowLeft className="h-4 w-4" /> All requests</button>
        <div className="bg-white border border-line rounded-xl p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-heading font-medium text-ink">{active.subject}</h2>
            <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${active.status === "resolved" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{active.status}</span>
          </div>
          <div className="mt-4 space-y-3">
            {(active.messages || []).map((m) => (
              <div key={m.id} data-testid="support-message" className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.from === "user" ? "bg-forest text-white" : "bg-stone-100 text-ink"}`}>
                  <p className="text-[11px] font-semibold opacity-80 mb-0.5">{m.from === "support" ? "Support" : m.author_name}</p>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <p className="text-[10px] opacity-60 mt-1">{formatDateTime(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 items-end">
            <textarea data-testid="support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Write a reply…" className="flex-1 border border-line rounded-lg px-3 py-2 text-sm resize-none" />
            <button data-testid="support-reply-send" disabled={busy || !reply.trim()} onClick={sendReply} className="bg-forest text-white px-4 py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center gap-1.5"><Send className="h-4 w-4" /> Send</button>
          </div>
        </div>
      </div>
    );
  }

  // List / compose view
  return (
    <div className="max-w-2xl space-y-4">
      <PriorityHeader />

      {composing ? (
        <div data-testid="support-compose" className="bg-white border border-line rounded-xl p-5 space-y-3">
          <h2 className="text-lg font-heading font-medium text-ink">New support request</h2>
          <input data-testid="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full border border-line rounded-lg px-3 py-2 text-sm" />
          <textarea data-testid="support-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="How can we help?" className="w-full border border-line rounded-lg px-3 py-2 text-sm resize-none" />
          <div className="flex gap-2">
            <button data-testid="support-submit" disabled={busy || !subject.trim() || !body.trim()} onClick={submitNew} className="bg-forest text-white px-5 py-2 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60">Send request</button>
            <button data-testid="support-cancel" onClick={() => setComposing(false)} className="border border-line text-ink px-5 py-2 rounded-lg font-medium hover:bg-stone-50">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-heading font-medium text-ink">Your requests</h2>
          <button data-testid="support-new-btn" onClick={() => setComposing(true)} className="bg-forest text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-forest-hover inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> New request</button>
        </div>
      )}

      {tickets.length === 0 && !composing ? (
        <div data-testid="support-empty" className="bg-white border border-line rounded-xl p-8 text-center">
          <LifeBuoy className="h-8 w-8 text-mute mx-auto mb-2" />
          <p className="text-sm text-mute">No support requests yet. Open one and we'll get right back to you.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} data-testid={`support-ticket-${t.id}`} onClick={() => openTicket(t.id)} className="w-full text-left bg-white border border-line rounded-xl p-4 hover:border-forest/40 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium text-ink truncate flex items-center gap-2">
                  {t.subject}
                  {t.unread_for_user && <span data-testid="support-unread-dot" className="inline-block w-2 h-2 rounded-full bg-terracotta" />}
                </p>
                <span className={`shrink-0 text-xs font-semibold rounded-full px-2.5 py-0.5 border inline-flex items-center gap-1 ${t.status === "resolved" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                  {t.status === "resolved" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{t.status}
                </span>
              </div>
              <p className="text-sm text-mute truncate mt-1">{t.last_from === "support" ? "Support: " : ""}{t.last_message}</p>
              <p className="text-[11px] text-mute mt-1">{formatDateTime(t.updated_at)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
